import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './FormPage.css';

// --- Configuration ---
const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbxmp0DaDNKEIewLI4xNcj-xJWhbCWBlUmq7gBdwf1rdlRRMrVIXMPg3wxtP2m3ENmBuIA/exec';
const REDIRECT_URL_BASE = '/merci';
const steps = [
    { id: 1, fieldId: "SITUATION_FAMILIALE", title: "Quelle est votre situation familiale ?", subtitle: "Pour calculer le montant de vos impôts", type: "cards", options: [{ label: "Célibataire", value: "Célibataire" }, { label: "Marié(e) / Pacsé(e)", value: "Marié(e) / Pacsé(e)" }, { label: "En concubinage", value: "En concubinage" }, { label: "Divorcé(e)", value: "Célibataire" }, { label: "Veuf(ve)", value: "Veuf(ve)" }] },
    { id: 2, fieldId: "AGE", title: "Quel âge avez-vous ?", subtitle: "Pour identifier les meilleurs dispositifs", type: "number", placeholder: "Votre âge...", buttonText: "Continuer" },
    { id: 3, fieldId: "NB_ENFANTS", title: "Combien d'enfants avez-vous à charge ?", subtitle: "Pour affiner votre simulation d'impôt", type: "counter", buttonText: "Continuer", allowZero: true, required: false },
    { id: 4, fieldId: "SITUATION_PRO", title: "Quelle est votre activité ?", subtitle: "Pour identifier les meilleurs dispositifs", type: "cards", options: [{ label: "Travailleur non salarié", value: "Travailleur non salarié" }, { label: "Salarié(e)", value: "Salarié(e)" }, { label: "Retraité(e)", value: "Retraité(e)" }, { label: "Sans activité professionnelle", value: "Sans activité" }] },
    { id: 5, fieldId: "REVENUS", title: "Quels sont vos revenus nets PAR MOIS avant impôts ?", subtitle: "Cette information est la base de calcul de votre simulation PER", type: "cards", options: [{ label: "Moins de 2500€", value: "< 2500€" }, { label: "Entre 2500€ et 5000€", value: "2500€ - 5000€" }, { label: "Entre 5000€ et 10000€", value: "5000€ - 10000€" }, { label: "Plus de 10000€", value: "> 10000€" }] },
    { id: 6, fieldId: "PROJET", title: "Décrivez-nous votre projet, dans quel but souhaitez-vous ouvrir votre PER ?", subtitle: "Facultatif", type: "textarea", placeholder: "Rédigez ici...", buttonText: "Continuer", required: false },
    { id: 7, fieldId: "NOM", title: "Votre Nom ?", subtitle: "Faisons connaissance", type: "text", placeholder: "Votre nom...", buttonText: "Continuer" },
    { id: 8, fieldId: "PRENOM", title: "Votre Prénom ?", subtitle: "Faisons connaissance", type: "text", placeholder: "Votre prénom...", buttonText: "Continuer" },
    { id: 9, fieldId: "CP", title: "Votre Code Postal ?", subtitle: "Saisissez votre réponse ici", type: "text", placeholder: "Ex: 75001", buttonText: "Continuer" },
    { id: 10, fieldId: "MAIL", title: "Quel est votre email ?", subtitle: "Nécessaire pour recevoir vos résultats", type: "email", placeholder: "nom@exemple.com", buttonText: "Continuer" },
    { id: 11, fieldId: "TELEPHONE", title: "Quel est votre numéro de téléphone ?", subtitle: "Un expert PER vous contacte dans la journée, pour réaliser votre simulation sur mesure", type: "tel", placeholder: "06 12 34 56 78", buttonText: "Voir mes résultats" }
];

const FormPage = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [error, setError] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countdown, setCountdown] = useState(3);

    const location = useLocation();
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const timeoutRef = useRef(null);
    const handleNextStepRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [currentStep]);

    useEffect(() => {
        if (isSuccess) {
            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        navigate(`${REDIRECT_URL_BASE}${location.search}`);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [isSuccess, navigate, location.search]);

    const updateProgress = () => ((currentStep + 1) / steps.length) * 100;

    const handleValidation = () => {
        const step = steps[currentStep];
        const value = formData[step.fieldId];

        if (step.required === false || (step.allowZero && value !== undefined)) return true;
        if (value === undefined || value === '' || value === null) { setError('Ce champ est requis'); return false; }
        if (step.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) { setError('Adresse email invalide'); return false; }
        if (step.type === 'tel' && !/^(?:(?:\+|00)?33|0)[1-9](?:\d{8})$/.test(value.replace(/[\s.\-()]/g, ''))) { setError('Numéro de téléphone invalide'); return false; }
        if (step.fieldId === 'CP' && !/^\d{5}$/.test(value)) { setError('Le code postal doit contenir 5 chiffres'); return false; }
        
        setError('');
        return true;
    };

    const handleSubmit = async () => {
        if (!handleValidation()) return;
        setIsSubmitting(true);

        let phoneValue = formData['TELEPHONE'] || '';
        phoneValue = phoneValue.replace(/[\s.\-()]/g, '');
        if (phoneValue.startsWith('0')) phoneValue = '+33' + phoneValue.substring(1);
        
        const payload = new FormData();
        payload.append('NOM', formData['NOM']);
        payload.append('PRENOM', formData['PRENOM']);
        payload.append('TELEPHONE', phoneValue);
        payload.append('MAIL', formData['MAIL']);
        payload.append('CP', formData['CP']);
        payload.append('SITUATION FAMILIALE', formData['SITUATION_FAMILIALE']);
        payload.append('AGE', formData['AGE']);
        payload.append('Nb ENFANTS A CHARGE', formData['NB_ENFANTS'] !== undefined ? String(formData['NB_ENFANTS']) : '0');
        payload.append('SITUATION PRO', formData['SITUATION_PRO']);
        payload.append('REVENUS NETS IMPOSABLES', formData['REVENUS']);
        payload.append('DESCRIPTION PROJET', formData['PROJET']);

        try {
            await fetch(GOOGLE_SHEETS_URL, {
                method: 'POST',
                body: payload,
                mode: 'no-cors',
            });
        } catch (e) {
            // On ignore l'erreur car no-cors ne donne pas de réponse.
        } finally {
            // Store data for Enhanced Conversions
            const enhancedConversionData = {
                email: formData['MAIL'],
                phone_number: phoneValue,
                first_name: formData['PRENOM'],
                last_name: formData['NOM'],
            };
            sessionStorage.setItem('enhancedConversionData', JSON.stringify(enhancedConversionData));

            setTimeout(() => {
                setIsSubmitting(false);
                setIsSuccess(true);
            }, 1500);
        }
    };

    const handleNextStep = () => {
        if (!handleValidation()) return;
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleSubmit();
        }
    };

    handleNextStepRef.current = handleNextStep;

    const handlePrevStep = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const handleSelectOption = (fieldId, value) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setFormData({ ...formData, [fieldId]: value });
        setError('');

        timeoutRef.current = setTimeout(() => {
            handleNextStepRef.current();
        }, 250);
    };

    const renderStepContent = () => {
        const step = steps[currentStep];
        switch (step.type) {
            case 'cards':
                return (
                    <div className="options">
                        {step.options.map((opt, idx) => (
                            <button key={idx} className={`option-btn ${formData[step.fieldId] === opt.value ? 'selected' : ''}`} onClick={() => handleSelectOption(step.fieldId, opt.value)}>
                                <div className="option-label">
                                    <div className="option-letter">{String.fromCharCode(65 + idx)}</div>
                                    <span>{opt.label}</span>
                                </div>
                                {formData[step.fieldId] === opt.value && '✓'}
                            </button>
                        ))}
                    </div>
                );
            case 'counter':
                const value = formData[step.fieldId] !== undefined ? formData[step.fieldId] : 0;
                return (
                    <>
                        <div className="counter-container">
                            <button className="counter-btn" onClick={() => setFormData({ ...formData, [step.fieldId]: Math.max(0, value - 1) })}>−</button>
                            <div className="counter-value">{value}</div>
                            <button className="counter-btn" onClick={() => setFormData({ ...formData, [step.fieldId]: value + 1 })}>+</button>
                        </div>
                        <button className="btn-primary" onClick={handleNextStep}>{step.buttonText || 'Continuer'} →</button>
                    </>
                );
            default:
                const InputComponent = step.type === 'textarea' ? 'textarea' : 'input';
                return (
                    <>
                        <InputComponent
                            ref={inputRef}
                            type={step.type === 'textarea' ? undefined : step.type}
                            className="input-field"
                            placeholder={step.placeholder || ''}
                            value={formData[step.fieldId] || ''}
                            onChange={(e) => setFormData({ ...formData, [step.fieldId]: e.target.value })}
                            onKeyPress={(e) => e.key === 'Enter' && handleNextStep()}
                        />
                        <button className="btn-primary" onClick={handleNextStep} disabled={isSubmitting}>
                            {isSubmitting ? <div className="loading-spinner"></div> : `${step.buttonText || 'Continuer'} →`}
                        </button>
                        {step.type !== 'textarea' && <div className="hint">Appuyez sur Entrée ↵</div>}
                    </>
                );
        }
    };

    if (isSuccess) {
        return (
            <div className="form-page-body">
                <div className="form-container">
                    <div className="success-screen">
                        <div className="success-icon">✓</div>
                        <h2 style={{ fontSize: '32px', marginBottom: '24px' }}>Félicitations !</h2>
                        <div style={{ background: 'linear-gradient(135deg, #f0fdfa, #ecfdf5)', padding: '24px', borderRadius: '16px', marginBottom: '24px' }}>
                            <p style={{ fontSize: '16px', color: '#0f766e', lineHeight: '1.6' }}>
                                Selon notre simulation, vous pourriez <strong>réduire votre fiscalité jusqu'à 45%</strong> et faire jusqu'à <strong>12,5% de plus-value</strong> sur l'ensemble de votre investissement.
                            </p>
                        </div>
                        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Un expert vous contactera dans la journée pour réaliser votre simulation personnalisée.</p>
                        <div id="redirectCountdown" style={{ background: '#dbeafe', padding: '16px', borderRadius: '12px', color: '#1e40af' }}>
                            Redirection en cours... <strong id="countdown">{countdown}</strong>s
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="form-page-body">
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${updateProgress()}%` }}></div></div>
            <div className="form-container">
                {currentStep === 0 && (
                    <div className="intro-text">
                        <p>Obtenez votre simulation PER personnalisée : capital estimé, effort d’épargne et économies d’impôts. Un conseiller validera vos résultats.</p>
                        <strong className="warning">⚠️ Pour une simulation fiable, merci de renseigner des informations exactes.</strong>
                    </div>
                )}
                {currentStep > 0 && <button className="btn-back" onClick={handlePrevStep}>← Retour</button>}
                <div className="step-indicator">{currentStep + 1} <span>/ {steps.length}</span></div>
                <h1>{steps[currentStep].title}</h1>
                {steps[currentStep].subtitle && <div className="subtitle">{steps[currentStep].subtitle}</div>}
                {renderStepContent()}
                {error && <div className="error-message show">{error}</div>}
            </div>
        </div>
    );
};

export default FormPage;